import { ChangeEvent, FC, PropsWithChildren, useEffect, useState } from 'react';
import { Button, Checkbox, Radio, Typography } from 'antd';

import { Drawer } from '@/shared/components/drawer';
import { Icon } from '@/shared/components/icon';
import { Row } from '@/shared/components/layout';
import { COLORS } from '@/shared/constants/colors';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { Tabs } from '@/shared/uikitV2/antd/Tabs';

import { Category } from './Category';
import { InviteDrawer } from './InviteDrawer';
import {
  isNextOwnerTab,
  ModalInviteUser,
  NextOwnerTabs,
  NextOwnerUser,
  SetInvitedUsers,
} from './type';

const { TabPane } = Tabs;

type BeneficiariesDrawerProps = {
  filteredUser: Array<NextOwnerUser>;
  invitedUsers: Array<string>;
  isModalOpen: boolean;
  label: string;
  notInvitedUser: Array<NextOwnerUser>;
  notRegisteredUser: Array<NextOwnerUser>;
  registeredUser: Array<NextOwnerUser>;
  searchValue: string;
  isSelectPersonMode?: boolean;
  onAddNewClick: () => Promise<void>;
  onSelectClick: VoidFunction;
  refetchNestOwners: VoidFunction;
  searchInputHandler: (e: ChangeEvent<HTMLInputElement>) => void;
  setInvitedUsers: SetInvitedUsers;
  setIsModalOpen: (arg: boolean) => void;
  handleSelect: (newValue: string[]) => void;
  selectedItems: string[];
  mode: 'multiple' | 'tags' | undefined;
};

const keys: Record<NextOwnerTabs, string> = {
  all: 'all',
  invited: 'invited',
  notInvited: 'notInvited',
  registered: 'registered',
};

export const BeneficiariesDrawer: FC<BeneficiariesDrawerProps> = ({
  filteredUser,
  invitedUsers,
  isModalOpen,
  label,
  notInvitedUser,
  notRegisteredUser,
  registeredUser,
  searchValue,
  selectedItems,
  isSelectPersonMode, // TODO for select beneficiaryId in pids
  handleSelect,
  // onAddNewClick,
  onSelectClick,
  refetchNestOwners,
  searchInputHandler,
  setInvitedUsers,
  setIsModalOpen,
  mode,
}) => {
  const router = useAppRouter();
  const [activeTab, setActiveTab] = useState<NextOwnerTabs>('all');

  const [modalInviteUserState, setModalInviteUserState] = useState<ModalInviteUser>({
    isOpen: false,
  });

  // After create new user change tab
  useEffect(() => {
    const { isInvited, isFastAdded } = router.query;
    const query = { ...router.query };
    delete query.isInvited;
    delete query.isFastAdded;

    if (isFastAdded === 'true') {
      setActiveTab(isInvited === 'true' ? 'invited' : 'notInvited');

      router.replace({
        pathname: router.pathname,
        query: query,
      });
    }
  }, [router]);

  const onSelect = (value: string | undefined) => {
    if (mode === 'multiple') {
      if (!value) {
        return;
      }

      const isAlreadySelected = selectedItems.includes(value);

      if (isAlreadySelected) {
        handleSelect(selectedItems.filter((item) => item !== value));
        return;
      }

      handleSelect(selectedItems.concat(value));
      return;
    }

    if (value) {
      handleSelect([value]);
      return;
    }

    handleSelect([]);
  };

  const getIsChecked = (value: string | undefined) => {
    if (!value) {
      return undefined;
    }

    if (mode === 'multiple') {
      return selectedItems.includes(value);
    }

    return undefined;
  };

  const TabsComponent = isSelectPersonMode ? (
    <SelectItemsWrapper handleSelect={handleSelect} value={selectedItems} mode={mode}>
      <Tabs
        activeKey={activeTab}
        defaultActiveKey="all"
        // onChange={(tabName) => {
        //   if (isNextOwnerTab(tabName)) {
        //     setActiveTab(tabName);
        //   }
        //   refetchNestOwners();
        // }}
        size="large"
        tabBarGutter={16}
        tabPosition="top"
        type="line"
        hidden={true}
      >
        <TabPane key={keys.all}>
          <Category
            isShowStatus
            dataSource={filteredUser}
            invitedUsers={invitedUsers}
            searchValue={searchValue}
            isSelectPersonMode={isSelectPersonMode}
            handleSelect={onSelect}
            searchInputHandler={searchInputHandler}
            setModalInviteUserState={setModalInviteUserState}
            mode={mode}
            getIsChecked={getIsChecked}
          />
        </TabPane>
      </Tabs>
    </SelectItemsWrapper>
  ) : (
    <SelectItemsWrapper handleSelect={handleSelect} value={selectedItems} mode={mode}>
      <Tabs
        activeKey={activeTab}
        defaultActiveKey="all"
        onChange={(tabName) => {
          if (isNextOwnerTab(tabName)) {
            setActiveTab(tabName);
          }

          refetchNestOwners();
        }}
        size="large"
        tabBarGutter={16}
        tabPosition="top"
        type="line"
        hidden={true}
      >
        <TabPane tab={`All (${filteredUser.length})`} key={keys.all}>
          <Category
            dataSource={filteredUser}
            handleSelect={onSelect}
            invitedUsers={invitedUsers}
            isShowStatus
            searchInputHandler={searchInputHandler}
            searchValue={searchValue}
            setModalInviteUserState={setModalInviteUserState}
            mode={mode}
            getIsChecked={getIsChecked}
          />
        </TabPane>

        <TabPane tab={`Registered (${registeredUser.length})`} key={keys.registered}>
          <Category
            dataSource={registeredUser}
            descriptionInfo={{
              title: 'What is Registered?',
              text: 'Family Member has been invited, and completed the registration in Owner.One. An algorithm for such Family Member can be created and activated. This algorithm can also be tested.',
            }}
            handleSelect={onSelect}
            invitedUsers={invitedUsers}
            searchInputHandler={searchInputHandler}
            searchValue={searchValue}
            setModalInviteUserState={setModalInviteUserState}
            mode={mode}
            getIsChecked={getIsChecked}
          />
        </TabPane>

        <TabPane tab={`Not Registered (${notRegisteredUser.length})`} key={keys.invited}>
          <Category
            dataSource={notRegisteredUser}
            descriptionInfo={{
              title: 'What is Not Registered?',
              text: `Family Member has been invited, but he hasn't completed the registration in Owner.One yet. Please, press the button "Resend invite" to repeat invite. An algorithm for such Family Member can be created and activated. But it will be on hold until registration is completed.`,
            }}
            handleSelect={onSelect}
            invitedUsers={invitedUsers}
            searchInputHandler={searchInputHandler}
            searchValue={searchValue}
            setModalInviteUserState={setModalInviteUserState}
            mode={mode}
            getIsChecked={getIsChecked}
          />
        </TabPane>

        <TabPane tab={`Not Invited (${notInvitedUser.length})`} key={keys.notInvited}>
          <Category
            dataSource={notInvitedUser}
            descriptionInfo={{
              title: 'What is Not Invited?',
              text: `Family Member hasn't been invited, and he hasn't completed the registration in Owner.One yet. Please, press the button "Send invite" to send an invitation. An algorithm for such Family Member can be created and activated. But it will be on hold until registration is completed.`,
            }}
            handleSelect={onSelect}
            invitedUsers={invitedUsers}
            searchInputHandler={searchInputHandler}
            searchValue={searchValue}
            setModalInviteUserState={setModalInviteUserState}
            mode={mode}
            getIsChecked={getIsChecked}
          />
        </TabPane>
      </Tabs>
    </SelectItemsWrapper>
  );

  return (
    <>
      <Drawer
        height="auto"
        placement="bottom"
        open={isModalOpen}
        styles={{
          body: {
            paddingBottom: 0,
          },
        }}
        onClose={() => setIsModalOpen(false)}
        closeIcon={false}
        footer={
          <Row gap={12} paddingX={14}>
            {/* <Button size="large" onClick={onAddNewClick} style={{ flex: 1 }}>
              Add New
            </Button> */}

            <Button
              type="primary"
              size="large"
              onClick={onSelectClick}
              disabled={!selectedItems.length}
              style={{ flex: 1 }}
            >
              Select
            </Button>
          </Row>
        }
      >
        <Row justifyContent="space-between" alignCenter nowrap>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {label}
          </Typography.Title>

          <Icon
            icon="close"
            cursorPointer
            onClick={() => {
              setIsModalOpen(false);
              localStorage.removeItem(`new-family-member-added`);
            }}
            color={COLORS.colorLink}
          />
        </Row>
        {TabsComponent}
      </Drawer>

      <InviteDrawer
        modalInviteUserInfo={modalInviteUserState}
        setInvitedUsers={setInvitedUsers}
        setModalInviteUserState={setModalInviteUserState}
      />
    </>
  );
};

const SelectItemsWrapper: FC<
  {
    mode: 'multiple' | 'tags' | undefined;
    handleSelect: (newValue: string[]) => void;
    value: string[];
  } & PropsWithChildren
> = ({ mode, children, handleSelect, value }) => {
  if (mode === 'multiple') {
    return (
      <Checkbox.Group
        onChange={(checkedValue) => handleSelect(checkedValue)}
        style={{ width: '100%', minWidth: '100%', display: 'inline-block', flex: 1 }}
        value={value}
      >
        {children}
      </Checkbox.Group>
    );
  } else {
    return (
      <Radio.Group
        onChange={(e) => handleSelect([e.target.value])}
        style={{ width: '100%', flex: 1 }}
        value={value[0]}
      >
        {children}
      </Radio.Group>
    );
  }
};
